// frontend/src/components/book/atelier/store/saveEngine.js
//
// MOTEUR DE SAUVEGARDE de l'atelier — un seul, pour le livre entier.
//
// Avant : un effet par type de contenu, declenche A CHAQUE changement sans
// delai, une file par page (rien n'empechait deux pages d'ecrire en meme
// temps), la couverture sauvee a part par son propre panneau, aucune
// nouvelle tentative, et l'erreur brute du serveur affichee telle quelle
// ("Trop de requetes. Reessayez dans une minute."). Maintenant :
//
//  - REGROUPEE : on attend un court silence (1,5 s ; 0,35 s pour un geste
//    ponctuel comme poser une photo, voir `meta.fast`) avant d'ecrire, avec
//    un plafond d'attente (8 s) pour qu'une longue saisie ne reste jamais
//    non enregistree ;
//  - SERIALISEE : jamais deux requetes en vol pour ce livre ;
//  - ROBUSTE : en cas d'echec, nouvelle tentative avec attente croissante
//    (1, 2, 4, 8, 15, 30 s), reprise immediate au retour du reseau ;
//  - SOBRE : trois etats seulement (saving / saved / offline), jamais un
//    message technique.
//
// Le moteur ne connait ni React ni le serveur : il lit le store, et passe par
// `io` pour ecrire (voir BookAtelierLuxe.js). C'est ce qui le rend testable.

import { BOOK_ACTIONS, listDirty, pageSignature, coverSignature } from './bookReducer';
import { planWrite } from './pageModel';

export const DEFAULT_ENGINE_CONFIG = {
  debounceMs: 1500,
  fastMs: 350,
  maxWaitMs: 8000,
  minGapMs: 120,
  backoffMs: [1000, 2000, 4000, 8000, 15000, 30000],
  // Le serveur a dit "trop de requetes" : inutile de reessayer avant ca.
  rateLimitMinMs: 10000
};

const realTimers = {
  setTimeout: (...args) => setTimeout(...args),
  clearTimeout: (...args) => clearTimeout(...args),
  now: () => Date.now()
};

/**
 * @param {object} deps
 * @param {{getState:Function, dispatch:Function}} deps.store  store du livre
 * @param {object} deps.io
 *   io.writePage(index, plan)  -> Promise  (plan : voir pageModel.planWrite)
 *   io.writeCover(cover)       -> Promise
 *   io.layoutsBySlug()         -> { [slug]: {id, kind} }
 *   io.afterPageSaved(index, plan)  (optionnel)
 *   io.afterCoverSaved()            (optionnel)
 */
export function createSaveEngine({ store, io, config = {}, timers = realTimers }) {
  const cfg = { ...DEFAULT_ENGINE_CONFIG, ...config };
  let timer = null;
  let running = null;
  let failures = 0;
  // Livre VERROUILLE (423 : une commande a ete payee) : le serveur refusera TOUTE ecriture. Reessayer en boucle ne
  // sert a rien (et martelait le serveur jusqu'a l'erreur 429) : on s'arrete, on le dit, on n'ecrit plus.
  let locked = false;
  let firstDirtyAt = null;
  let disposed = false;

  const setStatus = (status) => store.dispatch({ type: BOOK_ACTIONS.SET_STATUS, status });
  const dirtyList = () => listDirty(store.getState());

  function clearTimer() {
    if (timer != null) { timers.clearTimeout(timer); timer = null; }
  }

  function schedule(delay) {
    clearTimer();
    timer = timers.setTimeout(() => { timer = null; run(); }, Math.max(0, delay));
  }

  function retryDelay(error) {
    const step = cfg.backoffMs[Math.min(failures - 1, cfg.backoffMs.length - 1)];
    return error && error.status === 429 ? Math.max(step, cfg.rateLimitMinMs) : step;
  }

  async function writeUnit(unit) {
    if (unit.unit === 'cover') {
      const cover = store.getState().cover;
      const signature = coverSignature(cover);
      await io.writeCover(cover);
      store.dispatch({ type: BOOK_ACTIONS.MARK_SAVED, unit: 'cover', signature });
      if (io.afterCoverSaved) io.afterCoverSaved();
      return;
    }

    const state = store.getState();
    const model = state.pages[unit.index];
    const signature = pageSignature(model);
    const plan = planWrite(model, state.saved.pages[unit.index], io.layoutsBySlug());
    if (!plan) {
      // Le serveur est deja dans cet etat (page vierge jamais ecrite).
      store.dispatch({ type: BOOK_ACTIONS.MARK_SAVED, unit: 'page', index: unit.index, signature });
      return;
    }
    if (plan.op === 'unavailable') {
      // Catalogue de mises en page non charge : on ne PEUT pas ecrire, mais on
      // ne doit pas non plus declarer la page enregistree.
      const error = new Error('layout-unavailable');
      error.code = 'layout-unavailable';
      throw error;
    }
    await io.writePage(unit.index, plan);
    store.dispatch({ type: BOOK_ACTIONS.MARK_SAVED, unit: 'page', index: unit.index, signature });
    if (io.afterPageSaved) io.afterPageSaved(unit.index, plan);
  }

  function run() {
    if (running) return running;
    if (locked) { setStatus('locked'); return Promise.resolve({ ok: false, locked: true }); }
    clearTimer();
    running = (async () => {
      let ok = true;
      try {
        // Borne de securite : jamais de boucle sans fin si une page restait
        // "sale" apres son ecriture.
        for (let guard = 0; guard < 500; guard += 1) {
          if (disposed) return { ok: true };
          const dirty = dirtyList();
          if (dirty.length === 0) break;
          setStatus(failures > 0 ? 'offline' : 'saving');
          try {
            // eslint-disable-next-line no-await-in-loop
            await writeUnit(dirty[0]);
            failures = 0;
          } catch (error) {
            if (error && (error.status === 423 || error.bookLocked)) {
              locked = true;
              failures = 0;
              ok = false;
              setStatus('locked');
              break;
            }
            failures += 1;
            ok = false;
            setStatus('offline');
            schedule(retryDelay(error));
            break;
          }
          // Petite pause entre deux ecritures, seulement s'il en reste.
          if (dirtyList().length > 0) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => { timers.setTimeout(resolve, cfg.minGapMs); });
          }
        }
      } finally {
        running = null;
      }
      if (ok && dirtyList().length === 0) {
        firstDirtyAt = null;
        setStatus('saved');
      } else if (ok) {
        // Des modifications sont arrivees pendant la derniere ecriture.
        notify(); // eslint-disable-line no-use-before-define
      }
      return { ok };
    })();
    return running;
  }

  function notify() {
    if (disposed) return;
    if (locked) { setStatus('locked'); return; }
    const dirty = dirtyList();
    if (dirty.length === 0) {
      if (!running) {
        clearTimer();
        firstDirtyAt = null;
        const { status } = store.getState();
        // Retour a l'etat enregistre sans avoir rien ecrit (annuler avant
        // l'echeance) : on le dit, mais on n'invente rien au demarrage.
        if (status === 'saving' || status === 'offline') setStatus('saved');
      }
      return;
    }
    if (firstDirtyAt == null) firstDirtyAt = timers.now();
    setStatus(failures > 0 ? 'offline' : 'saving');
    if (running) return; // la boucle en cours reprendra ces modifications
    if (failures > 0) return; // une nouvelle tentative est deja programmee
    const last = store.getState().lastEdit;
    const wait = last && last.fast ? cfg.fastMs : cfg.debounceMs;
    schedule(Math.min(wait, firstDirtyAt + cfg.maxWaitMs - timers.now()));
  }

  /**
   * Ecrit TOUT de suite ce qui est en attente et rend la main quand c'est
   * fait. Resolu avec { ok:false } si le serveur est injoignable — a l'appelant
   * de renoncer (jamais enchainer une operation de structure sur des pages non
   * enregistrees).
   */
  async function flush() {
    if (disposed) return { ok: true };
    if (running) await running;
    if (locked) return { ok: false, locked: true };
    if (dirtyList().length === 0) return { ok: true };
    // Une attente de nouvelle tentative ne retient pas un flush explicite.
    const result = await run();
    return result || { ok: dirtyList().length === 0 };
  }

  /** Retour du reseau : on reessaie tout de suite plutot que d'attendre le delai. */
  function retryNow() {
    if (disposed || running || locked || dirtyList().length === 0) return;
    run();
  }

  function dispose() {
    disposed = true;
    clearTimer();
  }

  return { notify, flush, retryNow, dispose, hasPending: () => dirtyList().length > 0 || Boolean(running) };
}
